import { useEffect } from 'react';
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  AdditionalColumnsListDto,
  AllowedDomainListDto,
  BillingInvoiceContactCompany,
  OrganizationDto,
  OrganizationOverviewDto,
  OrganizationUsersGroupedDto,
  YourTeamDto,
  OrganizationListDto,
  UpdateOrganizationDto,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import { useUserSetupStore } from '@sib/auth';
import { CACHE_TIME } from '../constants';
import { SpendBrainTableName } from './preferences';

const setupOrganizationActionPermissions = ({
  organizationId,
  name,
  permissions,
}: {
  organizationId: string;
  name: string;
  permissions?: Record<string, number> | null;
}) => {
  const { setOrganization, setActionPermissions } =
    useUserSetupStore.getState();

  setOrganization({ name });
  setActionPermissions(permissions || {}, organizationId);
};

const useSyncOrganizationActionPermissions = (
  organizationId: string | undefined,
  data?: {
    id: string;
    name: string;
    permissions?: Record<string, number> | null;
  }
) => {
  useEffect(() => {
    if (!organizationId) return;

    const { actionPermissionsOrganizationId } = useUserSetupStore.getState();

    if (data?.id === organizationId) {
      if (actionPermissionsOrganizationId === organizationId) return;

      setupOrganizationActionPermissions({
        organizationId,
        name: data.name,
        permissions: data.permissions,
      });

      return;
    }

    if (actionPermissionsOrganizationId !== null)
      useUserSetupStore.setState({ actionPermissionsOrganizationId: null });
  }, [organizationId, data]);
};

export const useGetOrganizationOverview = (
  organizationId?: string,
  options?: UseQueryOptions<OrganizationOverviewDto>
): UseQueryResult<OrganizationOverviewDto> => {
  const query = useQuery({
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.OrganizationOverview,
      organizationId,
      organizationId,
    ],
    queryFn: async () => {
      const data =
        await api.internalPortal.organizationsControllerGetOrganizationOverview(
          organizationId || ''
        );

      setupOrganizationActionPermissions({
        organizationId: organizationId || data.id,
        name: data.name,
        permissions: data.permissions,
      });

      return data;
    },
    ...options,
  });

  useSyncOrganizationActionPermissions(organizationId, query.data);

  return query;
};

export const useGetOrganization = (
  organizationId?: string,
  options?: UseQueryOptions<OrganizationDto>
): UseQueryResult<OrganizationDto> => {
  const query = useQuery({
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.Organization, organizationId],
    queryFn: async () => {
      const data =
        await api.organization.organizationControllerGetOrganizationById(
          organizationId || ''
        );

      setupOrganizationActionPermissions({
        organizationId: organizationId || data.id,
        name: data.name,
        permissions: data.permissions,
      });

      return data;
    },
    ...options,
  });

  useSyncOrganizationActionPermissions(organizationId, query.data);

  return query;
};

export const useGetOrganizationById = (
  organizationId?: string,
  options?: UseQueryOptions<OrganizationDto>
): UseQueryResult<OrganizationDto> =>
  useQuery({
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.Organization, organizationId],
    queryFn: async () =>
      api.organization.organizationControllerGetOrganizationById(
        organizationId || ''
      ),
    ...options,
  });

export const useGetOrganizationUsers = (
  organizationId?: string,
  options?: UseQueryOptions<OrganizationUsersGroupedDto>
): UseQueryResult<OrganizationUsersGroupedDto> =>
  useQuery({
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.OrganizationUsers, organizationId],
    queryFn: async () =>
      await api.organization.userManagementControllerGetOrganizationUsers(
        organizationId || ''
      ),
    ...options,
  });

interface UseGetYourTeamPayload {
  organizationId?: string;
  isVectorOrganization: boolean | null;
}

export const useGetYourTeam = (
  { organizationId, isVectorOrganization }: UseGetYourTeamPayload,
  options?: UseQueryOptions<YourTeamDto>
): UseQueryResult<YourTeamDto> =>
  useQuery({
    enabled: !!organizationId && typeof isVectorOrganization === 'boolean',
    queryKey: [QueryKey.Resource.OrganizationTeamMembers, organizationId],
    queryFn: async () => {
      const data = await api.organization.organizationControllerGetYourTeam(
        organizationId || '',
        {
          billingContactProvider: isVectorOrganization
            ? BillingInvoiceContactCompany.Vector
            : BillingInvoiceContactCompany.Sib,
        }
      );

      return data;
    },
    ...options,
  });

export const useGetAllowedDomains = (
  organizationId?: string,
  options?: UseQueryOptions<AllowedDomainListDto>
): UseQueryResult<AllowedDomainListDto> =>
  useQuery({
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.OrganizationAllowedDomains, organizationId],
    queryFn: async () =>
      api.organization.organizationControllerFindAllowedDomains(
        organizationId || ''
      ),
    ...options,
  });

interface UseGetAdditionalColumnsPayload {
  organizationId?: string;
  tableName: SpendBrainTableName;
  options?: UseQueryOptions<AdditionalColumnsListDto>;
}

export const useGetAdditionalColumns = ({
  organizationId,
  tableName,
  options,
}: UseGetAdditionalColumnsPayload): UseQueryResult<AdditionalColumnsListDto> =>
  useQuery({
    enabled: !!organizationId && !!tableName,
    queryKey: [
      QueryKey.Resource.OrganizationAdditionalColumns,
      organizationId,
      tableName,
    ],
    queryFn: async () =>
      api.organization.organizationControllerGetAdditionalColumns(
        tableName,
        organizationId || ''
      ),
    ...options,
  });

export const useGetInternalPortalOrganizationSubsidiaries = (
  organizationId: string,
  query: Parameters<
    typeof api.internalPortal.organizationsControllerGetSubsidiaries
  >[1],
  options?: UseQueryOptions<OrganizationListDto>
): UseQueryResult<OrganizationListDto> =>
  useQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.InternalPortalOrganizations,
      organizationId,
      QueryKey.Operation.List,
      'subsidiaries',
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      api.internalPortal.organizationsControllerGetSubsidiaries(
        organizationId,
        query
      ),
  });

type ApiOrganizationSubsidiariesCsvExportQuery = Parameters<
  typeof api.internalPortal.organizationsControllerExportSubsidiaries
>[1];

export type OrganizationSubsidiariesCsvExportQuery = Omit<
  ApiOrganizationSubsidiariesCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportOrganizationSubsidiariesCsv = async (
  organizationId: string,
  query: OrganizationSubsidiariesCsvExportQuery
) =>
  api.internalPortal.organizationsControllerExportSubsidiaries(
    organizationId,
    query,
    { format: 'blob' }
  ) as Promise<Blob>;

export type UseExportOrganizationSubsidiariesCsvOptions = UseMutationOptions<
  Blob,
  Error,
  OrganizationSubsidiariesCsvExportQuery
> & {
  organizationId: string;
};

export const useExportOrganizationSubsidiariesCsv = ({
  organizationId,
  ...options
}: UseExportOrganizationSubsidiariesCsvOptions) =>
  useMutation({
    ...options,
    mutationFn: variables =>
      exportOrganizationSubsidiariesCsv(organizationId, variables),
  });

export const useGetInternalPortalOrganizationList = (
  query: Parameters<
    typeof api.internalPortal.organizationsControllerGetOrganizations
  >[0],
  options?: UseQueryOptions<OrganizationListDto>
): UseQueryResult<OrganizationListDto> =>
  useQuery({
    queryKey: [
      QueryKey.Resource.InternalPortalOrganizations,
      QueryKey.Operation.List,
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      api.internalPortal.organizationsControllerGetOrganizations(query),
    ...options,
  });

type ApiOrganizationsCsvExportQuery = Parameters<
  typeof api.internalPortal.organizationsControllerExportOrganizations
>[0];

export type OrganizationsCsvExportQuery = Omit<
  ApiOrganizationsCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportOrganizationsCsv = async (
  query: OrganizationsCsvExportQuery
) =>
  api.internalPortal.organizationsControllerExportOrganizations(query, {
    format: 'blob',
  }) as Promise<Blob>;

export const useExportOrganizationsCsv = (
  options?: Partial<
    UseMutationOptions<Blob, Error, OrganizationsCsvExportQuery>
  >
) =>
  useMutation({
    ...options,
    mutationFn: exportOrganizationsCsv,
  });

export const useInternalPortalUpdatedOrganization = (
  options?: Partial<
    UseMutationOptions<
      OrganizationOverviewDto,
      Error,
      { organizationId: string } & UpdateOrganizationDto
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.OrganizationOverview,
          variables.organizationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.List,
          variables.organizationId,
        ],
      });
      if (
        (variables.addVendorIds && variables.addVendorIds.length > 0) ||
        (variables.removeVendorIds && variables.removeVendorIds.length > 0)
      )
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalUnassignedToOrganizationVendors,
            QueryKey.Operation.List,
            variables.organizationId,
          ],
        });

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async ({
      organizationId,
      ...data
    }: { organizationId: string } & UpdateOrganizationDto) =>
      api.internalPortal.organizationsControllerUpdateOrganization(
        organizationId,
        data
      ),
  });
};
